<template>
    <Modal data-component="CartOffersModal"
           class="cart-offers-modal"
           size="lg">
        <template v-slot:header>
            <div class="modl-header">
                <ModalTabs :tabs="tabs"
                           :selected-tab="selectedTab"
                           @select-tab="selectTab"></ModalTabs>
            </div>
        </template>

        <template v-slot:body>
            <component :is="selectedTab.component"
                       v-if="selectedTab"
                       ref="current-tab-component"
                       :cart="cart"
                       @cancel="cancel" />
        </template>

        <template v-slot:footer>
            <div class="modl-footer">
                <SquareBtn size="lg"
                           class="cancel-btn"
                           @click="cancel">
                    {{ l('CANCEL') }}
                </SquareBtn>
                <SquareBtn :loading="saving"
                           class="ok-btn"
                           color="primary"
                           size="lg"
                           @click="ok">
                    {{ l('SAVE') }}
                </SquareBtn>
            </div>
        </template>
    </Modal>
</template>

<script>
import CartOffersCouponCodesTab from '@/epos/vue/modals/CartOffers/CartOffersCouponCodesTab';
import CartOffersTab from '@/epos/vue/modals/CartOffers/CartOffersTab';
import Modal from '@/vue/components/Modals/Modal';
import ModalTabs from '@/vue/components/Modals/ModalTabs';
import SquareBtn from '@/vue/components/SquareBtn';
import { CartOffersTabNameEnum } from '@/epos/vue/modals/CartOffers/CartOffersTabNameEnum';
import { cloneShallow } from '@/functions/clone';
import { CloseModalOnEscapeMixin } from '@/vue/mixins/CloseModalOnEscapeMixin';
import { container } from '@/container';
import { ModalPropsMixin } from '@/vue/mixins/ModalPropsMixin';
import { TabbedModalMixin } from '@/vue/mixins/TabbedModalMixin';

export default {
    components: {
        SquareBtn,
        Modal,
        ModalTabs,
        CartOffersCouponCodesTab,
        CartOffersTab,
    },

    mixins: [
        ModalPropsMixin,
        TabbedModalMixin,
        CloseModalOnEscapeMixin,
    ],

    props: {
        cart: {
            type: Object,
            required: true,
        },

        initialTabName: {
            type: String,
            default: null,
        },
    },

    data() {
        return {
            saving: false,
        };
    },

    async created() {
        const selectedOffers = cloneShallow(this.cart.selectedManualOfferIDs);
        const selectedCouponCodeOfferCodes = cloneShallow(this.cart.selectedCouponCodeOfferCodes);

        const allManualOrderOffers = await container.offerRepository.findAllManualOrderOffers();
        let couponCodeOffers = [];
        let selectedManualOrderOffers = {};
        let selectedCouponCodeOffers = {};

        // Check all the selectedManualOfferIDs and selectedCouponCodeOfferCodes in the cart.
        await Promise.all(
            Object.keys(selectedOffers).map(async (offerID) => {
                let offer = allManualOrderOffers.find((o) => o.ID === parseInt(offerID));
                if (offer) {
                    // This offer was already loaded in the possible manual order offers.
                    selectedManualOrderOffers[offerID] = selectedOffers[offerID];
                    return;
                }

                try {
                    offer = await container.offerRepository.find(offerID);
                    // We want to show that this offer is enabled. Add it to the UI.
                    if (selectedCouponCodeOfferCodes[offerID]) {
                        offer.code = selectedCouponCodeOfferCodes[offerID];
                    }
                    couponCodeOffers.push(offer);
                    selectedCouponCodeOffers[offerID] = selectedOffers[offerID];
                } catch (e) {
                    // Offer not found. A possible reason for this is that the device has not been synced to fetch
                    // the offer.
                    console.error(e);
                }
            })
        );

        this.$store.commit('cartOffers/setManualOrderOffers', allManualOrderOffers);
        this.$store.commit('cartOffers/setSelectedManualOrderOffers', selectedManualOrderOffers);
        this.$store.commit('cartOffers/setCouponCodeOffers', couponCodeOffers);
        this.$store.commit('cartOffers/setSelectedCouponCodeOffers', selectedCouponCodeOffers);
        this.$store.commit('cartOffers/setSelectedCouponCodeOfferCodes', selectedCouponCodeOfferCodes);

        this.tabs.push({
            name: CartOffersTabNameEnum.OFFERS,
            icon: 'fa-badge-percent',
            label: 'CART_OFFERS.DISCOUNTS_TAB_TITLE',
            component: 'CartOffersTab',
            ref: 'cart-offers-tab',
        });

        this.tabs.push({
            name: CartOffersTabNameEnum.OFFERS_COUPON_CODES,
            icon: 'fa-tags',
            label: 'CART_OFFERS.OFFER_CODES_TAB_TITLE',
            component: 'CartOffersCouponCodesTab',
            ref: 'cart-offers-codes-tab',
        });

        this.selectInitialTab();
    },

    methods: {
        selectInitialTab() {
            // If a specific tab was requested. Check if it exists and select it if so.
            if (this.initialTabName) {
                const initialTab = this.tabs.find((t) => t.name === this.initialTabName);
                if (initialTab) {
                    this.selectTab(initialTab);
                    return;
                }
            }

            // No specific tab was requested. Show the first tab.
            if (this.tabs.length > 0) {
                this.selectTab(this.tabs[0]);
            }
        },

        ok() {
            this.resolve({
                selectedOffers: this.$store.getters['cartOffers/selectedOffers'],
                selectedOfferCodes: this.$store.getters['cartOffers/selectedOfferCodes'],
            });
        },
    },
};
</script>
